import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Autosuggest from "react-autosuggest";
import { defaultTheme } from "react-autosuggest/dist/theme";
import { SketchPicker } from "react-color";
import NumberFormat from "react-number-format";
import reactCSS from "reactcss";
import ConfirmationModal from "../ConfirmationModal";
import Portal from "../Portal";
import { removeAllTags, thousandsSeparators } from "../../../../config/utils";
import { DYNAMIC_FIELD_TYPES } from "../../constants";
import { RichEditorPopup } from "../formFields/RichEditorPopup";

import { getVerbToken, matchVerbs, applyVerbToText, isFirstWordValid } from "../../../../utils/recommendationVerbs";

export const InputCell = ({
    keyItem,
    rowIndex,
    config,
    handleCellFocus,
    handleCellValueChange,
    setCellValueChange,
    rowData,
    inputValue,
    suggestionArray = [],
    hasPrefixSuggestionMatch = false
}) => {
    const hasThousandsSeparator = !["year", "major_renovation_year", "installed_year", "service_life", "occurrence"].includes(keyItem);
    const [inputChange, setInputChange] = useState("");
    const [showNoteModal, setShowNoteModal] = useState(true);
    const [fieldSuggestions, setFieldSuggestions] = useState([]);
    const ignoreNextBlurRef = useRef(false);

    const isMemoField = config[keyItem]?.isMemoField;
    const hasSuggestionArray = Array.isArray(suggestionArray) && suggestionArray.length > 0;
    const getCurrentCellValue = () => {
        if (config[keyItem]?.isDynamicField) {
            return rowData?.dynamic_fields?.[keyItem]?.value ?? "";
        }
        const accessor = config[keyItem]?.accessor;
        const cellValue = typeof accessor === "function" ? accessor(rowData) : rowData?.[keyItem];
        return cellValue ?? "";
    };
    const getCurrentDateCellValue = () => {
        const value = config[keyItem]?.isDynamicField ? rowData?.dynamic_fields?.[keyItem]?.value : rowData?.[keyItem];
        return value && value !== "-" ? value : "";
    };
    const getSelectedDate = value => {
        if (!value || value === "-") return null;
        const parsedDate = moment(value, ["YYYY-MM-DD", "MM-DD-YYYY", moment.ISO_8601], true);
        return parsedDate.isValid() ? parsedDate.toDate() : null;
    };

    useEffect(() => {
        const type = config[keyItem]?.type;
        if (config[keyItem]?.isDynamicField) {
            const fieldObject = rowData?.dynamic_fields;
            const type = config[keyItem]?.type;
            const { thousand_separator = false, prefix = "", suffix = "" } = config[keyItem]?.rules;

            let data = fieldObject?.[keyItem]?.value ?? "";
            if (thousand_separator || type === DYNAMIC_FIELD_TYPES.CURRENCY) {
                data = thousandsSeparators(data);
            }
            if ((prefix || suffix) && data) {
                data = `${prefix}${data}${suffix}`;
            }
            setInputChange(data);
        } else if (type === "date") {
            setInputChange(getCurrentDateCellValue());
        } else if (type === "string" || type === "text" || type === "memo") {
            setInputChange(getCurrentCellValue());
        }
    }, []);

    useEffect(() => {
        if (hasSuggestionArray && !hasPrefixSuggestionMatch) {
            setFieldSuggestions(suggestionArray);
        } else {
            setFieldSuggestions([]);
        }
    }, [hasSuggestionArray, hasPrefixSuggestionMatch, suggestionArray]);

    const handleMemoChange = data => {
        setInputChange(removeAllTags(data));
        handleCellValueChange(data, keyItem);
        setTimeout(() => {
            setCellValueChange(keyItem, rowIndex);
        }, 100);
    };

    const updateValue = value => {
        setInputChange(value);
        handleCellValueChange(value, keyItem);
    };

    const commitValue = () => {
        if (ignoreNextBlurRef.current) {
            ignoreNextBlurRef.current = false;
            return;
        }
        setCellValueChange(keyItem, rowIndex);
    };

    const resetValue = () => {
        const currentValue = config[keyItem]?.type === "date" ? getCurrentDateCellValue() : getCurrentCellValue();
        ignoreNextBlurRef.current = true;
        setInputChange(currentValue);
        handleCellValueChange(currentValue, keyItem);
        handleCellFocus("", "");
    };

    const renderSuggestion = suggestion => <div>{suggestion}</div>;
    const renderSuggestionsContainer = ({ containerProps, children }) => {
        if (!children || !hasPrefixSuggestionMatch || !fieldSuggestions.length) return <div {...containerProps}>{children}</div>;
        return (
            <div {...containerProps}>
                <div className="verb-suggestion-header">Approved verbs</div>
                {children}
            </div>
        );
    };
    const getSuggestionValue = suggestion => (hasPrefixSuggestionMatch ? applyVerbToText(inputChange, suggestion) : suggestion);
    const onSuggestionsFetchRequested = ({ value }) => {
        if (!hasPrefixSuggestionMatch) {
            setFieldSuggestions(suggestionArray);
            return;
        }
        if (isFirstWordValid(value, suggestionArray)) {
            setFieldSuggestions([]);
            return;
        }
        const { word, prefix } = getVerbToken(value);
        setFieldSuggestions(matchVerbs(word, suggestionArray, !!prefix));
    };
    const onSuggestionsClearRequested = () => setFieldSuggestions([]);
    const handleSuggestionSelected = (event, { suggestionValue }) => {
        updateValue(suggestionValue);
        if (hasPrefixSuggestionMatch) return;
        ignoreNextBlurRef.current = true;
        setTimeout(() => {
            setCellValueChange(keyItem, rowIndex);
        }, 0);
    };

    if (config[keyItem]?.type === "date") {
        const isError = config[keyItem]?.rules?.error;
        const read_only = config[keyItem]?.rules?.read_only;
        const isLocked = config[keyItem]?.isLocked;
        const placeholder = config[keyItem]?.label || "Select date";
        const selectedDate = getSelectedDate(inputChange);
        return (
            <DatePicker
                autoComplete="nope"
                placeholderText={placeholder}
                selected={selectedDate}
                onChange={date => {
                    const iso = date ? moment(date).format("YYYY-MM-DD") : "";
                    setInputChange(iso);
                    handleCellValueChange(iso, keyItem);
                    setTimeout(() => {
                        setCellValueChange(keyItem, rowIndex);
                    }, 100);
                }}
                dateFormat="MM-dd-yyyy"
                inputReadOnly
                showMonthDropdown
                showYearDropdown
                dropdownMode="scroll"
                maxDate={moment().add(100, "years").toDate()}
                minDate={moment().subtract(100, "years").toDate()}
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                disabled={read_only || isLocked}
                className={`${isError ? "error-border" : ""} custom-input form-control${read_only || isLocked ? " grayed-out" : ""}`}
                autoFocus
            />
        );
    }

    return (
        <>
            {keyItem === "color_code" || config[keyItem]?.type === DYNAMIC_FIELD_TYPES.COLOR ? (
                <ColorPicker
                    colorCode={config[keyItem]?.isDynamicField ? getCurrentCellValue() : inputValue[keyItem]}
                    keyItem={keyItem}
                    rowIndex={rowIndex}
                    setCellValueChange={setCellValueChange}
                    rowData={rowData}
                    handleCellValueChange={handleCellValueChange}
                    handleCellFocus={handleCellFocus}
                />
            ) : isMemoField ? (
                showNoteModal ? (
                    <Portal
                        body={
                            <RichEditorPopup
                                value={keyItem === "notes" ? rowData?.note_html : inputChange || ""}
                                label={config[keyItem]?.label}
                                key={keyItem}
                                onCancel={() => {
                                    handleCellFocus("", "");
                                    setShowNoteModal(false);
                                }}
                                saveData={data => {
                                    handleMemoChange(data);
                                }}
                            />
                        }
                        onCancel={() => {
                            handleCellFocus("", "");
                            setShowNoteModal(false);
                        }}
                    />
                ) : (
                    <input value={inputChange} className="custom-input form-control" disabled />
                )
            ) : config[keyItem]?.type === "string" || config[keyItem]?.type === "text" ? (
                hasSuggestionArray ? (
                    <Autosuggest
                        suggestions={fieldSuggestions}
                        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={onSuggestionsClearRequested}
                        onSuggestionSelected={handleSuggestionSelected}
                        inputProps={{
                            value: inputChange ?? "",
                            className: "custom-input form-control",
                            placeholder: config[keyItem]?.label || "",
                            autoFocus: true,
                            onChange: (e, { newValue }) => updateValue(newValue),
                            onBlur: commitValue,
                            onKeyDown: e => {
                                if (e.key === "Enter") commitValue();
                                if (e.key === "Escape") resetValue();
                            }
                        }}
                        renderSuggestion={renderSuggestion}
                        renderSuggestionsContainer={renderSuggestionsContainer}
                        getSuggestionValue={getSuggestionValue}
                        shouldRenderSuggestions={value => !hasPrefixSuggestionMatch || !isFirstWordValid(value, suggestionArray)}
                        theme={{
                            ...defaultTheme,
                            container: "line-edit-autosuggest dropdown-arrow-icon react-autosuggest__container",
                            suggestion: hasPrefixSuggestionMatch
                                ? "react-autosuggest__suggestion verb-suggestion-row"
                                : "react-autosuggest__suggestion"
                        }}
                    />
                ) : (
                    <input
                        value={inputChange}
                        className="custom-input form-control"
                        onChange={e => updateValue(e.target.value)}
                        onBlur={commitValue}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                commitValue();
                            }
                            if (e.key === "Escape") {
                                resetValue();
                            }
                        }}
                        autoFocus
                    />
                )
            ) : (
                <NumberFormat
                    value={config[keyItem]?.isDynamicField ? inputChange : parseFloat(inputValue[keyItem])}
                    className="custom-input form-control"
                    thousandSeparator={config[keyItem]?.isDynamicField ? config[keyItem]?.rules?.thousand_separator : hasThousandsSeparator}
                    decimalScale={config[keyItem]?.isDynamicField ? config[keyItem]?.rules?.decimal_scale || 0 : config[keyItem]?.decimalScale || 0}
                    onValueChange={values => {
                        const { value, sourceInfo } = values;
                        if (sourceInfo?.source !== "prop") {
                            handleCellValueChange(parseFloat(value), keyItem);
                        }
                    }}
                    allowNegative={config[keyItem]?.isDynamicField ? config[keyItem]?.rules?.allow_negative || false : false}
                    isAllowed={
                        keyItem === "installed_year"
                            ? ({ value }) => {
                                  if (value === "" || value == null) return true;
                                  const numericValue = parseInt(value, 10);
                                  return !isNaN(numericValue) && numericValue <= new Date().getFullYear() + 5;
                              }
                            : undefined
                    }
                    onKeyPress={e => e.key === "Enter" && commitValue()}
                    onBlur={commitValue}
                    onKeyDown={e => {
                        if (e.key === "Escape" || e.keyCode === 27) {
                            resetValue();
                        }
                    }}
                    prefix={config[keyItem]?.rules?.prefix || ""}
                    suffix={config[keyItem]?.rules?.suffix || ""}
                    autoFocus
                    disabled={config[keyItem]?.rules?.read_only || false}
                />
            )}
        </>
    );
};

const ColorPicker = ({ colorCode, setCellValueChange, rowData, handleCellFocus, handleCellValueChange, keyItem, rowIndex }) => {
    const [showPicker, setPicker] = useState(true);
    const [selectedColor, setColor] = useState(colorCode);
    const [showClearConfirm, setClearConfirm] = useState(false);
    const styles = reactCSS({
        default: {
            color: {
                width: "40px",
                height: "15px",
                borderRadius: "3px",
                background: selectedColor
            },
            popover: {
                position: "absolute",
                zIndex: "3"
            },
            cover: {
                position: "fixed",
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px"
            },
            swatch: {
                padding: "6px",
                background: "#ffffff",
                borderRadius: "2px",
                cursor: "pointer",
                display: "inline-block",
                boxShadow: "0 0 0 1px rgba(0,0,0,.2)"
            }
        }
    });
    const handleSave = async () => {
        setPicker(false);
        await handleCellValueChange(selectedColor, keyItem);
        setCellValueChange(keyItem, rowIndex);
    };
    const handleClear = async () => {
        setClearConfirm(false);
        setPicker(false);
        setColor("");
        await handleCellValueChange("", keyItem);
        setCellValueChange(keyItem, rowIndex);
    };
    const handleCancel = () => {
        setColor(colorCode);
        setPicker(false);
        handleCellFocus("", "");
    };
    const renderConfirmationModal = () => {
        if (!showClearConfirm) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to remove this color?"}
                        message={"This action cannot be reverted, are you sure?"}
                        onNo={() => setClearConfirm(false)}
                        onYes={handleClear}
                    />
                }
                onCancel={() => setClearConfirm(false)}
            />
        );
    };

    // Cell click would reopen the picker after Save/Cancel.
    return (
        <div onClick={e => e.stopPropagation()}>
            {renderConfirmationModal()}
            <div className="color-palette-flx">
                <div className={"cursor-hand"}>
                    {selectedColor ? (
                        <>
                            {selectedColor}
                            <span
                                className="color-box-common"
                                style={{
                                    background: selectedColor
                                }}
                            ></span>
                        </>
                    ) : (
                        "-"
                    )}
                </div>
            </div>
            {showPicker && (
                <>
                    <div className="btn-out-table">
                        <button type="button" className="btn-out-cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button type="button" className="btn-out-cancel" onClick={() => setClearConfirm(true)}>
                            Clear
                        </button>
                        <button type="button" className="btn-out-save" onClick={handleSave}>
                            Save
                        </button>
                    </div>
                    <div style={styles.popover}>
                        <SketchPicker
                            color={selectedColor || ""}
                            className="sketch-picker-zoom"
                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                            onChange={color => setColor(color?.hex || "")}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
